import { EFinancingProgram } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { Divider, Grid, Typography } from '@mui/material'
import { useCreditRslaTerms } from '@src/containers/EditWorksheet/editWorksheet-hooks'
import { EditCreditRslaWorksheetDto, EditCreditRslaWorksheetDtoSchema } from '@src/data/types/CreditRslaWorksheet'
import { useEffect, useMemo } from 'react'
import {
  Control,
  FieldError,
  FieldErrorsImpl,
  Merge,
  UseFormRegister,
  UseFormReset,
  UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextField, SelectComponent } from '../../../components'
import { Constants, CreditApplication } from '../../../data/types'

interface Props {
  control: Control<EditCreditRslaWorksheetDto>
  currentCreditApplication: CreditApplication
  worksheet: EditCreditRslaWorksheetDto
  reset: UseFormReset<EditCreditRslaWorksheetDto>
  register: UseFormRegister<EditCreditRslaWorksheetDto>
  watch: UseFormWatch<EditCreditRslaWorksheetDto>
  errors: Merge<FieldError, FieldErrorsImpl<EditCreditRslaWorksheetDto>> | undefined
  creditApplicationInterestRate: number
  minFirstPaymentDate: Date
  maxFirstPaymentDate: Date
}

const TransactionForm = ({
  control,
  worksheet,
  reset,
  register,
  errors,
  creditApplicationInterestRate,
  currentCreditApplication,
  minFirstPaymentDate,
  maxFirstPaymentDate,
}: Props) => {
  const { t } = useTranslation()

  useEffect(() => {
    const today = formatDate(new Date())
    if (worksheet) {
      reset({
        ...worksheet,
        status: EditCreditRslaWorksheetDtoSchema.getDefault().status,
        deliveryOn: formatDate(worksheet.deliveryOn) < today ? today : worksheet.deliveryOn,
      })
    }
  }, [worksheet, reset])

  const paymentFrequencyOptions = useMemo(
    () =>
      Constants.financingProgramConfigs[EFinancingProgram.CreditRsla].supportedPaymentFrequencies.map((item) => ({
        label: t(`EPaymentFrequency.${item}`, { ns: 'enum' }),
        value: item,
      })),
    [t],
  )
  const availableTerms = useCreditRslaTerms(currentCreditApplication)

  return (
    <>
      <Divider style={{ fontSize: 25 }}>
        <Typography variant="h4" component="span" gutterBottom>
          Transaction
        </Typography>
      </Divider>
      <Grid container spacing={3} sx={{ mt: 1, p: 2 }} size={12}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheetCommon.amountRequested')}
            error={errors?.amountRequested}
            {...register('amountRequested')}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.lienAmount')}
            {...register(`lienAmountFee`)}
            error={errors?.lienAmountFee}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.brokerAdminFee')}
            {...register(`brokerAdminFee`)}
            error={errors?.brokerAdminFee}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.roadside')}
            {...register(`roadsideAssistanceAmount`)}
            error={errors?.roadsideAssistanceAmount}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.merchantReferralFee')}
            {...register(`merchantReferralFee`)}
            error={errors?.merchantReferralFee}
          />
        </Grid>
        <Grid
          sx={{ flexDirection: 'row' }}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <ControlledStringDateOnlyPicker
            name="deliveryOn"
            control={control}
            label={t('worksheet.deliveryOn')}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.lenderFee}
            {...register('lenderFee')}
            disabled
            label={t('worksheet.lenderAdminFee')}
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.protectionPlan')}
            {...register(`insuranceFee`)}
            error={errors?.insuranceFee}
          />
        </Grid>
      </Grid>
      <Typography variant="h6" component="div" sx={{ paddingLeft: 2 }} gutterBottom>
        {t('worksheet.funding')}
      </Typography>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }} size={12}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            label={t('worksheet.paymentAmount')}
            error={errors?.paymentAmountForFrequency}
            {...register('paymentAmountForFrequency')}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={availableTerms.map((item) => ({
              label: `${item} ${t('worksheet.month')}`,
              value: item,
            }))}
            label={t('worksheet.term')}
            {...register('term')}
            error={errors?.term}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={paymentFrequencyOptions}
            label={t('worksheet.paymentFrequency')}
            {...register('paymentFrequency')}
            error={errors?.paymentFrequency}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField value={creditApplicationInterestRate} label={t('worksheet.interestRate')} disabled />
        </Grid>

        <Grid
          sx={{ flexDirection: 'row' }}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <ControlledStringDateOnlyPicker
            name="firstPaymentOn"
            control={control}
            label={t('worksheet.firstPaymentOn')}
            minDate={minFirstPaymentDate}
            maxDate={maxFirstPaymentDate}
            fullWidth
          />
        </Grid>
      </Grid>
    </>
  )
}

export default TransactionForm
